import { Button } from "@/shared";
import * as Sentry from "@sentry/react";
import type { ComponentProps, ReactNode } from "react";
import { toast } from "react-toastify";

type ErrorBoundaryProps = {
  children: ReactNode;
  feature: string;
  fallback?: ReactNode;
  toast?: string;
  skipFallback?: boolean;
  callback?: () => void;
};

/**
 * Check if an error is a chunk load error (404 from old deployment)
 */
export function isChunkLoadError(error: Error | unknown): boolean {
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : String(error || "");

  return (
    errorMessage.includes("Failed to fetch dynamically imported module") ||
    errorMessage.includes("Loading chunk") ||
    errorMessage.includes("Loading CSS chunk") ||
    errorMessage.includes("ChunkLoadError") ||
    errorMessage.includes("Failed to load module") ||
    errorMessage.includes("net::ERR_") ||
    errorMessage.includes("LiveblocksProvider is missing") ||
    errorMessage.includes("Unable to preload CSS")
  );
}

/**
 * Handle chunk load errors by reloading the page
 */
export function handleChunkLoadError(): void {
  // Check if we've already attempted a reload to prevent infinite loops
  const reloadAttempted = sessionStorage.getItem("chunk-reload-attempted");
  if (reloadAttempted) {
    sessionStorage.removeItem("chunk-reload-attempted");
    return;
  }

  // Mark that we're attempting a reload
  sessionStorage.setItem("chunk-reload-attempted", "true");

  // Small delay to ensure error is fully logged, then reload
  setTimeout(() => {
    window.location.reload();
  }, 100);
}

const ErrorBoundary = ({
  children,
  feature,
  toast: toastMessage,
  skipFallback,
  fallback,
  callback,
}: ErrorBoundaryProps) => {
  // Custom fallback that checks for chunk errors
  const fallbackRenderer: ComponentProps<
    typeof Sentry.ErrorBoundary
  >["fallback"] = ({ error }) => {
    // Check if this is a chunk load error (deployment mismatch)
    if (isChunkLoadError(error)) {
      handleChunkLoadError();
      // Return empty fragment - reload will happen
      return <></>;
    }

    // For non-chunk errors, show the normal error UI
    // Ensure we return a ReactElement
    if (skipFallback) {
      return <></>;
    }
    return (fallback || <ErrorMessage />) as React.ReactElement;
  };

  return (
    <Sentry.ErrorBoundary
      fallback={fallbackRenderer}
      beforeCapture={(scope, error) => {
        // Check if this is a chunk load error (deployment mismatch)
        if (isChunkLoadError(error)) {
          handleChunkLoadError();
          // Don't send to Sentry for chunk errors
          return;
        }

        scope.setTag("feature", feature);
        if (toastMessage) toast.error(toastMessage);
        if (callback) callback();
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default ErrorBoundary;

const ErrorMessage = () => {
  const handleBtnClick = () => window.location.reload();
  return (
    <div className="tw-fixed tw-left-0 tw-top-1/2 tw-flex tw-w-full -tw-translate-y-1/2 tw-flex-col tw-items-center tw-justify-center tw-p-4">
      <p className="tw-mb-6 tw-mt-0 tw-text-center tw-font-sans tw-text-2xl tw-font-bold tw-text-blue-base">
        Oops...
      </p>
      <p className="tw-text-md tw-mb-4 tw-mt-0 tw-text-center tw-font-sans tw-font-semibold tw-text-blue-base">
        Something went wrong, just hit the button below.
      </p>
      <Button
        variant="primary"
        title="Refresh"
        size="medium"
        onClick={handleBtnClick}
      />
    </div>
  );
};
